import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import toast from 'react-hot-toast';
import posthog from 'posthog-js';
import { clearCrmCache } from '../modules/crm/api/queryClient';
import { tgQueryClient } from '../utils/tgQueryClient';

const identifyPostHogUser = (user, organization) => {
  posthog.identify(user._id, {
    email: user.email,
    name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    role: user.role,
    org_id: organization?._id,
    org_name: organization?.name,
  });
};

// Create context
const AuthContext = createContext();

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_TOKEN: 'SET_TOKEN',
  SET_ERROR: 'SET_ERROR',
  LOGOUT: 'LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.SET_TOKEN:
      return {
        ...state,
        token: action.payload,
      };
    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const api = useApi();

  // Update token in API instance when it changes
  useEffect(() => {
    api.updateToken(state.token);
  }, [state.token, api]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return;
      }

      try {
        const response = await api.get('/api/v1/auth/me');
        const restoredUser = response.data.data.user;
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: restoredUser });
        dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: token });
        const org = JSON.parse(localStorage.getItem('organization') || 'null');
        identifyPostHogUser(restoredUser, org);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    };

    checkAuth();
  }, []);

  // Login function with enhanced debugging
  const login = async (email, password, rememberMe = false, recaptchaToken = null) => {
    try {
      console.log('🔐 AuthContext login called with:', { 
        email, 
        password: password ? '***' : 'empty', 
        rememberMe,
        baseURL: api.baseURL 
      });
      
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      // Clear any existing tokens first
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('organization');
      // Clear switch session flags on normal login
      localStorage.removeItem('isMainAdminSwitch');
      localStorage.removeItem('mainAdminSwitchUserId');

      const response = await api.post('/api/v1/auth/login', {
        email,
        password,
        rememberMe,
        recaptchaToken,
      });

      console.log('✅ Login API response:', response.data);
      const { token, refreshToken, user, organization } = response.data.data;

      // Store tokens
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);

      // Update token in API instance
      api.updateToken(token);

      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: token });

      // Store organization in localStorage for access across components
      if (organization) {
        localStorage.setItem('organization', JSON.stringify(organization));
      }

      // Notify in-tab listeners (CRM useAuthToken hook) that auth state
      // changed. The native 'storage' event only fires on OTHER tabs, so we
      // need this custom event to gate same-tab queries on token presence.
      try { window.dispatchEvent(new Event('auth-token-changed')); } catch (_e) {}

      identifyPostHogUser(user, organization);

      console.log('🎉 Login successful for user:', user.email);
      toast.success(`Welcome back, ${user.firstName}!`);
      
      return { 
        success: true, 
        user,
        organization
      };
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      const message = error.response?.data?.error?.message || 
                     error.response?.data?.message || 
                     error.message || 
                     'Login failed';
      
      console.error('Error message:', message);
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await api.post('/api/v1/auth/register', userData);

      const { token, refreshToken, user } = response.data.data;

      // Store tokens
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);

      // Update token in API instance
      api.updateToken(token);

      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: token });

      try { window.dispatchEvent(new Event('auth-token-changed')); } catch (_e) {}

      identifyPostHogUser(user, null);

      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      // Check for error message in the correct path (error.response.data.error.message)
      let message = error.response?.data?.error?.message || 
                   error.response?.data?.message || 
                   'Registration failed';
      
      // If there are validation details, show the first one or the main message
      if (error.response?.data?.error?.details && error.response.data.error.details.length > 0) {
        message = error.response.data.error.details[0] || message;
      }
      
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Google OAuth login function
  const googleLogin = async (idToken) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await api.post('/api/v1/auth/google', {
        idToken,
        isSignup: false,
      });

      const { token, refreshToken, user, organization } = response.data.data;

      // Store tokens
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);

      // Update token in API instance
      api.updateToken(token);

      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: token });

      // Store organization in localStorage for access across components
      if (organization) {
        localStorage.setItem('organization', JSON.stringify(organization));
      }

      try { window.dispatchEvent(new Event('auth-token-changed')); } catch (_e) {}

      identifyPostHogUser(user, organization);

      toast.success('Google login successful!');
      
      return { 
        success: true, 
        user,
        organization
      };
    } catch (error) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || 'Google login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Google OAuth signup function
  const googleSignup = async (idToken, organizationName) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await api.post('/api/v1/auth/google', {
        idToken,
        isSignup: true,
        organizationName,
      });

      const { token, refreshToken, user, organization } = response.data.data;

      // Store tokens
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);

      // Update token in API instance
      api.updateToken(token);

      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: user });
      dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: token });

      // Store organization in localStorage for access across components
      if (organization) {
        localStorage.setItem('organization', JSON.stringify(organization));
      }

      try { window.dispatchEvent(new Event('auth-token-changed')); } catch (_e) {}

      identifyPostHogUser(user, organization);

      toast.success('Google signup successful!');
      
      return { 
        success: true, 
        user,
        organization
      };
    } catch (error) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || 'Google signup failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Logout function. Best-effort across every cache surface so the next user
  // on this browser starts fresh: server session, axios auth header, token +
  // org localStorage keys, CRM TanStack Query cache, TG host react-query
  // cache, plus any session-scoped flags (currency cache, OAuth pivots).
  const logout = async () => {
    try {
      // Call logout endpoint
      await api.post('/api/v1/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('organization');
      // Clear switch session flags
      localStorage.removeItem('isMainAdminSwitch');
      localStorage.removeItem('mainAdminSwitchUserId');

      // Notify in-tab listeners that auth state changed (token removed).
      try { window.dispatchEvent(new Event('auth-token-changed')); } catch (_e) {}

      // Clear token from API instance
      api.updateToken(null);

      // Nuke the TG host's react-query (v3) cache so any previously fetched
      // /me, /organization, time-tracking, etc. don't briefly flash to the
      // next user before the new session re-fetches.
      try {
        tgQueryClient.cancelQueries();
        tgQueryClient.clear();
        tgQueryClient.removeQueries();
      } catch (e) {
        console.error('Failed to clear TG query cache:', e);
      }

      posthog.reset();

      // Nuke the CRM module's TanStack Query (v5) cache + user-scoped storage
      // (selectedProjectId, organization, currency session cache, OAuth pivots).
      try { clearCrmCache(); } catch (e) { console.error('Failed to clear CRM cache:', e); }

      // Update state
      dispatch({ type: AUTH_ACTIONS.LOGOUT });

      toast.success('Logged out successfully');

      // Force a full page reload to /login. Provider in-memory state
      // (SelectedProjectProvider's projectId, ActiveCallProvider's call,
      // AuthContext user, etc.) survives a SPA route change — only a fresh
      // document load guarantees every cache is gone. The 401 interceptor
      // does this for token-expiry; we do the same here so user-initiated
      // logout has identical "clean slate" semantics. setTimeout(0) lets the
      // toast paint first.
      setTimeout(() => {
        try { window.location.href = '/login'; } catch (_e) { /* test env */ }
      }, 0);
    }
  };

  // Forgot password function
  const forgotPassword = async (email) => {
    try {
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      await api.post('/api/v1/auth/forgot-password', { email });

      toast.success('Password reset email sent!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || 'Failed to send reset email';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Reset password function
  const resetPassword = async (token, newPassword) => {
    try {
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      await api.post('/api/v1/auth/reset-password', { token, newPassword });

      toast.success('Password reset successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || 'Password reset failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Change password function
  const changePassword = async (currentPassword, newPassword) => {
    try {
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      await api.post('/api/v1/auth/change-password', {
        currentPassword,
        newPassword,
      });

      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || 'Password change failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Update profile function
  const updateProfile = async (profileData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const response = await api.put('/api/v1/auth/me', profileData);

      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: response.data.data.user });

      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error?.message || error.response?.data?.message || 'Profile update failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/api/v1/auth/refresh', {
        refreshToken,
      });

      const { token } = response.data.data;

      // Update stored token
      localStorage.setItem('token', token);
      api.updateToken(token);

      dispatch({ type: AUTH_ACTIONS.SET_TOKEN, payload: token });

      return { success: true };
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout the user
      logout();
      return { success: false };
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Context value
  const value = {
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    login,
    register,
    googleLogin,
    googleSignup,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    updateProfile,
    refreshToken,
    clearError,
    isAuthenticated: !!state.user && !!state.token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
